// TREASURY v2 — batch 2's rebuild of TreasuryBalances (UI rebuild; drawn when lib/ui-v2.ts switches batch 2 on).
//
// Astra's screen (02-screen-review/treasury), under the operator's ruling of 26 Oct 2026 ("closer to that version"):
// the head with Move money as the one gold primary (Adjust balance beside it — its door on Treasury, ruling C1), one
// register — Account · Book balance · Outside the books · the chevron — grouped by rail, Astra's foot, and
// Convert and Wallet register under it. The money-safety lines stay in their lightest form: the rate book unread (one
// line), "To book" (money outside the books, one amber line), and a failed refresh (its rows stay, marked "Old read",
// with no count and no To-book line drawn from them).
//
// KEPT FROM TODAY'S SCREEN (screens/TreasuryBalances.tsx; the logic copied to components/v2/treasury/logic.ts): the
// query and its key, the desk switch, readState, the old-shape answer as a failed read, the groups' order and the
// quiet split (now "N more"), /treasury?rail= opening and marking its row, the wallet roll-up with "≈" / "≥" / `—`, the
// stale-price amber, a running account's "owed to …" with its sign, and every link: Money movements per account (in
// the row's expansion), Adjust balance. Account detail stays the row's expansion (N-036 held).
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { readState, offlineFailure } from '../../lib/read-state';
import { DeskSwitch, useDesk, DESK_LABEL } from '../../components/DeskSwitch';
import { EmptyState, Notice, PageHead, Panel, ReadFailed, SkeletonRows, StateLine } from '../../components/v2/kit';
import { useState } from 'react';
import { assetRates, coinsText, orderGroups, stripTotals, toBook } from '../../components/v2/treasury/logic';
import { Box, FocusRail, RollUpFigure, ToBookLine, ValuationBanner, ValuationNote } from '../../components/v2/treasury/figures';
import { fmtAmount } from '../../lib/format';
import { GroupRows, RegisterHead, rowCount } from '../../components/v2/treasury/register';

export function TreasuryV2() {
  // Which desk — DubaiOTC only since the IndiaOTC wind-down; the switch draws nothing with one desk in scope.
  const [desk, setDesk] = useDesk();
  const q = useQuery({ queryKey: ['treasury', 'balances', desk], queryFn: () => api.treasuryBalances(desk) });
  const [params] = useSearchParams();
  const focus = params.get('rail');
  const state = readState(q);
  const retry = () => q.refetch();
  const [all, setAll] = useState(false);

  const head = (
    <PageHead
      title="Wallets"
      sub="Where the desk’s money sits"
      actions={(
        <>
          <DeskSwitch value={desk} onChange={setDesk} />
          {/* Every door of the screen in its head (the audit, 1 Nov 2026: Convert and Wallet register sat under the long register). */}
          <Link to="/wallets" className="btn-secondary px-4 text-body">Wallet register</Link>
          <Link to="/swap" className="btn-secondary px-4 text-body">Convert</Link>
          <Link to="/treasury/adjust" className="btn-secondary px-4 text-body">Adjust balance</Link>
          <Link to="/treasury/rebalance" className="btn-primary px-4 text-body">Move money</Link>
        </>
      )}
    />
  );
  // Offline, the box is grey: the read is waiting, not refused (lib/read-state.ts offlineFailure; ReadFailed says the wait).
  const failed = <Box tone={offlineFailure(q.error) ? 'neutral' : 'bad'}><ReadFailed tone="bad" what="the treasury" error={q.error} onRetry={retry} /></Box>;

  // A failed FIRST read: the failure and Try again, nothing else.
  if (state === 'failed' && !q.data) return <>{head}{failed}</>;
  if (state === 'waiting' || !q.data) {
    return (
      <>
        {head}
        {q.fetchStatus === 'paused' && <Notice tone="neutral">Offline — waiting to read.</Notice>}
        <Panel flush><SkeletonRows rows={6} what="the treasury" /></Panel>
      </>
    );
  }
  const data = q.data;
  // The grouped shape has been served since 4 Sept 2026: an old-shape answer is a failed read, not a second screen.
  if (!data.groups) {
    return (
      <>
        {head}
        <Box tone="bad">
          <StateLine tone="bad" more="The books answered in an old shape. Refresh, or deploy the worker."
            action={<button type="button" className="btn-link text-body" onClick={retry}>Try again</button>}>
            Couldn&rsquo;t read the treasury — old answer from the books.
          </StateLine>
        </Box>
      </>
    );
  }

  // A failed REFRESH keeps its rows (marked "Old read"); nothing is claimed from them — no count, no To-book line,
  // no "nothing held".
  const fresh = state === 'known' ? 'known' : 'old';
  const groups = orderGroups(data.groups);
  const valuation = data.valuation;
  const records = groups.reduce((n, g) => n + rowCount(g, valuation), 0);
  const deskName = DESK_LABEL[desk];
  const rates = assetRates(groups, valuation);
  // THE TOTALS (restored 1 Nov 2026): the desk's currencies one figure each and its coins as one estimate — one compact
  // strip at the head of the register, never claimed from a refresh that failed.
  const strip = stripTotals(data.pocket_totals ?? data.totals ?? {}, rates, valuation);
  const anyMore = groups.some((g) => rowCount(g, valuation) > 0);

  return (
    <FocusRail.Provider value={focus}>
      {head}
      {fresh === 'old' && failed}
      <ValuationBanner valuation={valuation} onRetry={retry} />
      {fresh === 'known' && <ToBookLine items={toBook(groups, valuation)} desk={deskName} />}

      {groups.length === 0
        ? fresh === 'known' && <Panel><EmptyState>Nothing held yet.</EmptyState></Panel>
        : (
          <section aria-label="Treasury accounts" className="overflow-hidden rounded-[10px] border border-stroke bg-panel">
            {fresh === 'known' && (strip.fiat.length > 0 || strip.crypto) && (
              // THE TOTALS AS LABELLED CELLS (his "one fact per place", 1 Nov 2026): each currency its own cell — its code (and
              // "owed" when below zero) as the label, the figure at figure size under it; the coins the same, their count and
              // the estimate's age as the cell's quiet line and what has no price as its own amber line.
              <div aria-label="Totals" className="grid grid-cols-2 gap-x-6 gap-y-3 border-b border-stroke px-4 py-4 sm:flex sm:flex-wrap lg:px-5">
                {strip.fiat.map(([a, v]) => (
                  <div key={a} className="min-w-0">
                    <div className="text-label font-medium uppercase tracking-wider text-tg-hint">{v < 0 ? `${a} · owed` : a}</div>
                    <div className="mt-1 whitespace-nowrap font-mono tabular text-figure font-semibold">{fmtAmount(Math.abs(v), a)}</div>
                  </div>
                ))}
                {strip.crypto && (
                  <div className="col-span-2 min-w-0 sm:col-span-1">
                    <div className="text-label font-medium uppercase tracking-wider text-tg-hint">Coins · {coinsText(strip.coins)}</div>
                    <div className="mt-1 whitespace-nowrap"><RollUpFigure r={strip.crypto} size="figure" /></div>
                    <ValuationNote r={strip.crypto} strip />
                  </div>
                )}
              </div>
            )}
            <RegisterHead />
            {groups.map((g) => <GroupRows key={g.id} group={g} valuation={valuation} desk={deskName} fresh={fresh} rates={rates} all={all} />)}
            {fresh === 'known' && (
              <p className="flex flex-wrap items-center justify-between gap-x-4 border-t border-stroke px-4 py-3 text-meta text-tg-hint lg:px-5">
                <span>{records === 1 ? '1 record' : `${records} records`}</span>
                {anyMore && <button type="button" className="btn-link text-meta" aria-pressed={all} onClick={() => setAll(!all)}>{all ? 'Show fewer' : 'Show all'}</button>}
              </p>
            )}
          </section>
        )}

    </FocusRail.Provider>
  );
}
